"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.FormulaEvaluationError = void 0;
exports.evaluateFormula = evaluateFormula;
exports.toBoolean = toBoolean;
class FormulaEvaluationError extends Error {
    constructor(message) {
        super(message);
        this.name = 'FormulaEvaluationError';
    }
}
exports.FormulaEvaluationError = FormulaEvaluationError;
const DEFAULT_FUNCTIONS = {
    concat: (...args) => args.map(arg => String(arg ?? '')).join(''),
    concatenate: (...args) => args.map(arg => String(arg ?? '')).join(''),
    add: (...args) => args.reduce((sum, arg) => sum + toNumber(arg), 0),
    sum: value => toNumberArray(value).reduce((sum, item) => sum + item, 0),
    min: value => Math.min(...toNonEmptyNumberArray(value, 'min')),
    max: value => Math.max(...toNonEmptyNumberArray(value, 'max')),
    average: value => {
        const items = toNonEmptyNumberArray(value, 'average');
        return items.reduce((sum, item) => sum + item, 0) / items.length;
    },
    subtract: (left, right) => toNumber(left) - toNumber(right),
    multiply: (...args) => args.reduce((sum, arg) => sum * toNumber(arg), 1),
    divide: (left, right) => toNumber(left) / toNumber(right),
    equals: (left, right) => left === right,
    notEquals: (left, right) => left !== right,
    not: value => !toBoolean(value),
    greaterThan: (left, right) => toNumber(left) > toNumber(right),
    lessThan: (left, right) => toNumber(left) < toNumber(right),
    coalesce: (...args) => args.find(arg => arg !== null && arg !== undefined),
    if: (condition, whenTrue, whenFalse) => toBoolean(condition) ? whenTrue : whenFalse,
    join: (value, separator = ',') => Array.isArray(value)
        ? value.map(item => String(item ?? '')).join(String(separator))
        : String(value ?? ''),
    includes: (value, needle) => Array.isArray(value)
        ? value.includes(needle)
        : String(value ?? '').includes(String(needle ?? '')),
    now: () => new Date().toISOString(),
    formatDate: (value, locale = 'en-US', options) => formatDate(value, locale, options),
    'format-date': (value, locale = 'en-US', options) => formatDate(value, locale, options),
};
function evaluateFormula(formula, context) {
    if (!formula)
        return null;
    switch (formula.type) {
        case 'value':
            return formula.value;
        case 'path':
            return readPath(context.data, formula.path);
        case 'array':
            return (formula.arguments ?? []).map(arg => evaluateFormula(arg.formula, context));
        case 'object':
            return evaluateArgumentsObject(formula.arguments ?? [], context);
        case 'record':
            return evaluateArgumentsObject(formula.entries ?? [], context);
        case 'or':
            return (formula.arguments ?? []).some(arg => toBoolean(evaluateFormula(arg.formula, context)));
        case 'and':
            return (formula.arguments ?? []).every(arg => toBoolean(evaluateFormula(arg.formula, context)));
        case 'switch':
            for (const branch of formula.cases ?? []) {
                if (toBoolean(evaluateFormula(branch.condition, context))) {
                    return evaluateFormula(branch.formula, context);
                }
            }
            return evaluateFormula(formula.default, context);
        case 'function':
            return evaluateFunction(formula, context);
        case 'apply': {
            const applied = context.formulas?.[formula.name];
            if (!applied) {
                throw new FormulaEvaluationError(`Unknown formula "${formula.name}"`);
            }
            const args = evaluateArgumentsObject(formula.arguments ?? [], context);
            return evaluateFormula(applied, {
                ...context,
                data: { ...context.data, Args: args },
            });
        }
    }
}
function toBoolean(value) {
    if (typeof value === 'boolean')
        return value;
    if (typeof value === 'number')
        return value !== 0 && Number.isFinite(value);
    if (typeof value === 'string')
        return value.trim().length > 0;
    return Boolean(value);
}
function evaluateFunction(formula, context) {
    if (formula.name === 'jsonPath') {
        const [value, path] = evaluateFunctionArguments(formula, context);
        if (typeof path !== 'string') {
            throw new FormulaEvaluationError('jsonPath path must be a string');
        }
        return readJsonPath(value, path);
    }
    if (formula.name === 'getFormula' || formula.name === 'getCustomFormula') {
        const [name, ...args] = evaluateFunctionArguments(formula, context);
        if (typeof name !== 'string' || !name) {
            throw new FormulaEvaluationError(`${formula.name} name must be a string`);
        }
        const target = context.formulas?.[name];
        if (!target) {
            throw new FormulaEvaluationError(`Unknown formula "${name}"`);
        }
        return evaluateFormula(target, {
            ...context,
            data: { ...context.data, Args: args },
        });
    }
    const functionKey = formula.package
        ? `${formula.package}.${formula.name}`
        : formula.name;
    const fn = context.functions?.[functionKey] ??
        context.functions?.[formula.name] ??
        DEFAULT_FUNCTIONS[functionKey] ??
        DEFAULT_FUNCTIONS[formula.name];
    if (!fn) {
        throw new FormulaEvaluationError(`Unknown function "${functionKey}"`);
    }
    return fn(...evaluateFunctionArguments(formula, context));
}
function evaluateFunctionArguments(formula, context) {
    return (formula.arguments ?? []).map(arg => evaluateFormula(arg.formula, context));
}
function evaluateArgumentsObject(args, context) {
    return Object.fromEntries(args.map((arg, index) => [
        arg.name ?? String(index),
        evaluateFormula(arg.formula, context),
    ]));
}
function readPath(data, path) {
    let current = data;
    for (const segment of path) {
        if (current === null || current === undefined)
            return undefined;
        if (typeof segment === 'number') {
            if (!Array.isArray(current))
                return undefined;
            current = current[segment];
            continue;
        }
        if (typeof current !== 'object')
            return undefined;
        current = current[segment];
    }
    return current;
}
function readJsonPath(value, path) {
    if (path === '$')
        return value;
    if (!path.startsWith('$.')) {
        throw new FormulaEvaluationError('jsonPath must start with "$."');
    }
    const segments = path
        .slice(2)
        .replace(/\[(\d+)\]/g, '.$1')
        .split('.')
        .filter(Boolean)
        .map(segment => (/^\d+$/.test(segment) ? Number(segment) : segment));
    return readPath({ root: value }, ['root', ...segments]);
}
function toNumber(value) {
    const number = typeof value === 'number' ? value : Number(value);
    if (!Number.isFinite(number)) {
        throw new FormulaEvaluationError(`Expected finite number, got ${value}`);
    }
    return number;
}
function toNumberArray(value) {
    if (Array.isArray(value)) {
        return value.map(toNumber);
    }
    return [toNumber(value)];
}
function toNonEmptyNumberArray(value, functionName) {
    const items = toNumberArray(value);
    if (items.length === 0) {
        throw new FormulaEvaluationError(`${functionName} requires values`);
    }
    return items;
}
function formatDate(value, locale, options) {
    const date = value instanceof Date ? value : new Date(String(value));
    if (!Number.isFinite(date.getTime())) {
        throw new FormulaEvaluationError(`Invalid date: ${value}`);
    }
    const resolvedLocale = typeof locale === 'string' ? locale : 'en-US';
    const resolvedOptions = isRecord(options)
        ? options
        : undefined;
    return new Intl.DateTimeFormat(resolvedLocale, resolvedOptions).format(date);
}
function isRecord(value) {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
}
